import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useNavigate } from "react-router-dom";

/**
 * Universal cap-reached upsell modal — listens for `pb:cap-reached` window
 * events dispatched by the axios interceptor when the backend returns
 * `{status: 402, detail: {error: "cap_reached", feature, plan, cap, used, message}}`.
 *
 * Always shows the Photo Bridge wordmark so clients & photographers never
 * see an un-branded interstitial.
 */
const FEATURE_LABELS = {
  ai_cents: { name: "AI credits", icon: "✨", cta: "See AI-heavy plans" },
  video_renders: { name: "Marketing videos", icon: "🎬", cta: "Unlock more renders" },
  emails: { name: "Client emails", icon: "✉️", cta: "Raise the email cap" },
  zip_downloads: { name: "Bulk downloads", icon: "📦", cta: "Lift the download cap" },
  proxy_gb: { name: "Video streaming", icon: "📹", cta: "More streaming bandwidth" },
};

export default function UpsellModal() {
  const [state, setState] = useState(null); // {feature, plan, cap, used, message} | null
  const navigate = useNavigate();

  useEffect(() => {
    const onCap = (e) => setState(e.detail || null);
    window.addEventListener("pb:cap-reached", onCap);
    return () => window.removeEventListener("pb:cap-reached", onCap);
  }, []);

  if (!state) return null;
  const meta = FEATURE_LABELS[state.feature] || { name: state.feature, icon: "⚡", cta: "See plans" };
  const pctUsed = state.cap > 0 ? Math.min(100, Math.round((state.used / state.cap) * 100)) : 100;

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); }}>
      <DialogContent data-testid="upsell-modal" className="max-w-md">
        <DialogHeader className="pt-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-2xl">
              {meta.icon}
            </div>
            <div>
              <DialogTitle className="text-xl font-display" data-testid="upsell-title">
                You've hit this month's {meta.name} cap
              </DialogTitle>
              <p className="text-xs text-stone-500 mt-0.5">
                {state.plan?.charAt(0).toUpperCase() + state.plan?.slice(1)} plan · resets on the 1st
              </p>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-stone-300" data-testid="upsell-message">
            {state.message || "Upgrade to keep going, or wait until your next billing cycle resets the counter."}
          </p>
          <div>
            <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
              <span>{meta.name}</span>
              <span data-testid="upsell-usage">{state.used?.toLocaleString?.() ?? state.used} / {state.cap?.toLocaleString?.() ?? state.cap}</span>
            </div>
            <div className="h-2 rounded-full bg-stone-900 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${pctUsed}%` }} data-testid="upsell-progress"/>
            </div>
          </div>
        </div>
        <DialogFooter className="pt-4 sm:justify-between gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={() => setState(null)}
            data-testid="btn-upsell-dismiss"
            className="border-white/10 text-stone-300 hover:text-white"
          >
            <X className="w-4 h-4 mr-2"/>Not now
          </Button>
          <Button
            data-testid="btn-upsell-upgrade"
            onClick={() => { setState(null); navigate("/pricing"); }}
            className="bg-amber-500 text-stone-950 hover:bg-amber-400"
          >
            <TrendingUp className="w-4 h-4 mr-2"/>{meta.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
